import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, CheckCircle, AlertCircle, Send, Star } from '../components/icons';
import { supabase } from '../lib/supabase';

const TRAINING_TYPES = [
  { value: '7-day-new-model-nlp', label: '7-Day New Model of NLP' },
  { value: 'nlp-os', label: 'NLP OS' },
  { value: 'classical-nlp-practitioner', label: 'Classical NLP Practitioner Certification' },
];

const TRAINING_DAYS = [1, 2, 3, 4, 5, 6, 7];

interface WinsPageProps {
  trainingTypeOverride?: string;
  trainingLabelOverride?: string;
  trainingDays?: number[];
  heading?: string;
  description?: string;
}

export function WinsPage({
  trainingTypeOverride,
  trainingLabelOverride,
  trainingDays = TRAINING_DAYS,
  heading = 'What was your win today?',
  description = 'Take a moment to reflect on your training today. Share your biggest insight, breakthrough, or moment of clarity — we read every single one.',
}: WinsPageProps = {}) {
  const [name, setName] = useState('');
  const [trainingType, setTrainingType] = useState('7-day-new-model-nlp');
  const [trainingDay, setTrainingDay] = useState(1);
  const [winText, setWinText] = useState('');
  const [keyTakeaway, setKeyTakeaway] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (winText.trim().length < 10) {
      setError('Please share your win — at least a sentence or two.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: insertError } = await supabase
        .from('training_wins')
        .insert({
          participant_name: name.trim(),
          training_type: trainingTypeOverride || trainingType,
          training_day: trainingDay,
          win_text: winText.trim(),
          key_takeaway: keyTakeaway.trim() || null,
        });

      if (insertError) throw insertError;

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting win:', err);
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setName('');
    if (!trainingTypeOverride) setTrainingType('7-day-new-model-nlp');
    setTrainingDay(1);
    setWinText('');
    setKeyTakeaway('');
    setSubmitted(false);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-chocolate-900 via-chocolate-800 to-chocolate-900 relative overflow-hidden" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Ambient glow */}
      <motion.div
        className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(201,169,97,0.1) 0%, transparent 70%)' }}
        animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(107,155,90,0.1) 0%, transparent 70%)' }}
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-8 pb-6">
        <img
          src="/nlp_os_logo_off_white_creme_bg_.png"
          alt="NLP OS"
          className="h-10 md:h-12 object-contain object-left"
        />
        <div className="hidden md:flex items-center gap-2 px-4 py-1.5 rounded-full border border-cream-200/20 bg-white/5 backdrop-blur-sm">
          <Sparkles className="w-3.5 h-3.5 text-gold-light" />
          <span className="text-xs tracking-[0.2em] uppercase text-cream-200/70 font-medium">Daily Wins</span>
        </div>
      </header>

      {/* Body */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-2xl"
        >
          <div className="text-center mb-8">
            <p className="text-xs tracking-[0.3em] uppercase text-sage-400/80 font-medium mb-4">
              End of Day Reflection
            </p>
            <h1
              className="text-cream-100 leading-tight mb-3"
              style={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 'clamp(26px, 3.5vw, 44px)',
                fontWeight: 500,
                letterSpacing: '-0.01em',
              }}
            >
              {heading}
            </h1>
            <p className="text-cream-200/50 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
              {description}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="freud-card-elevated p-10 text-center"
                style={{ background: 'rgba(255,255,255,0.95)' }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-5"
                >
                  <CheckCircle className="w-8 h-8 text-sage-600" />
                </motion.div>
                <h2 className="freud-heading-xl text-chocolate-900 mb-3">Thank you for sharing!</h2>
                <p className="text-chocolate-500 mb-6 max-w-md mx-auto">
                  Your win from Day {trainingDay} has been received. We love reading these — keep them coming every day of the training.
                </p>
                <button
                  onClick={handleReset}
                  className="freud-btn-secondary inline-flex items-center gap-2"
                >
                  Submit Another Win
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="freud-card-elevated p-6 md:p-8"
                style={{ background: 'rgba(255,255,255,0.95)' }}
              >
                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-chocolate-700 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    className="freud-input"
                    disabled={submitting}
                  />
                </div>

                {/* Training type + Day */}
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-chocolate-700 mb-1.5">Training Program</label>
                    {trainingTypeOverride ? (
                      <div className="freud-input flex items-center text-chocolate-600">
                        {trainingLabelOverride}
                      </div>
                    ) : (
                      <select
                        value={trainingType}
                        onChange={(e) => setTrainingType(e.target.value)}
                        className="freud-input cursor-pointer"
                        disabled={submitting}
                      >
                        {TRAINING_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-chocolate-700 mb-1.5">Training Day</label>
                    <div className="flex gap-2 flex-wrap">
                      {trainingDays.map((day) => (
                        <button
                          key={day}
                          onClick={() => setTrainingDay(day)}
                          disabled={submitting}
                          className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${
                            trainingDay === day
                              ? 'bg-sage-600 text-white shadow-md'
                              : 'bg-cream-100 text-chocolate-500 hover:bg-cream-200'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Win text */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-chocolate-700 mb-1.5">
                    Your Win Today <span className="text-chocolate-400 font-normal">(required)</span>
                  </label>
                  <textarea
                    value={winText}
                    onChange={(e) => setWinText(e.target.value)}
                    placeholder="What was your biggest win, insight, or breakthrough from today's training?"
                    rows={4}
                    className="freud-input resize-none"
                    disabled={submitting}
                  />
                  <p className="text-xs text-chocolate-400 mt-1">{winText.length} characters</p>
                </div>

                {/* Key takeaway */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-chocolate-700 mb-1.5">
                    Key Takeaway <span className="text-chocolate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={keyTakeaway}
                    onChange={(e) => setKeyTakeaway(e.target.value)}
                    placeholder="One sentence that captures your biggest insight"
                    className="freud-input"
                    disabled={submitting}
                  />
                </div>

                {/* Submit */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="freud-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit My Win
                    </>
                  )}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <p className="relative z-10 text-xs tracking-[0.2em] uppercase text-cream-200/20 mt-10">
          humanpotentialos.com
        </p>
      </div>
    </div>
  );
}
